import i18next from 'i18next'
import {Chart, ChartOptions, TooltipModel} from 'chart.js'
import {LayoutPosition} from 'chart.js/types/layout'
import moment from 'moment/moment'
import {drawCtxCenterText, getCurrencySymbol, formatCurrency} from '@/utils'
import {getFontFamilyName} from '@/utils/css-styles-utils'
import {
    Currencies,
    DATE_FORMAT,
    DEFAULT_CHART_CONFIG,
    DENSITY_CHART_CONFIGS,
    TITLE_DAY_MONTH_FORMAT
} from '@/constants'
import {colors} from '@/constants/theme/colors'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'

// ============================================================================
// TOOLTIP TYPE DEFINITIONS
// ============================================================================

/**
 * Extended HTMLDivElement interface for tooltip elements with hover state tracking
 */
interface TooltipElement extends HTMLDivElement {
    _isHovered: boolean
}

/**
 * Data structure for settlement tooltip details
 */
interface SettlementDetail {
    transactionDate: string | Date
    settlementAmount: number
    currency: Currencies
}

/**
 * Full data object for settlement tooltips
 */
interface SettlementTooltipData {
    settlementAmount: number
    settlementDate: string | Date
    details: SettlementDetail[]
    currency: Currencies
}

/**
 * Full data object for payment tooltips
 */
interface PaymentTooltipData {
    date: string | Date
    amount: number
    status: string
    currency: Currencies
}

/**
 * Full data object for payment count tooltips
 */
interface PaymentCountTooltipData {
    date: string | Date
    count: number
    status: string
}

/**
 * Table header configuration for safe DOM construction
 */
interface TableHeaderConfig {
    text: string
    align: 'left' | 'right' | 'center'
}

/**
 * Table cell configuration for safe DOM construction
 */
interface TableCellConfig {
    text: string
    align: 'left' | 'right' | 'center'
    style?: Partial<CSSStyleDeclaration>
}

/**
 * Table row configuration for safe DOM construction
 */
interface TableRowConfig {
    cells: TableCellConfig[]
}

// ============================================================================
// TOOLTIP STYLES & CONSTANTS
// ============================================================================

const TOOLTIP_STYLES = {
    wrapper: {
        background: '#3d3d3d',
        color: 'white',
        borderRadius: '12px',
        padding: '10px',
        minWidth: '240px',
        fontSize: '13px',
        fontFamily: 'inherit'
    },
    header: {
        width: '100%',
        boxSizing: 'border-box' as const
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse' as const,
        marginTop: '4px'
    },
    link: {
        color: '#317e72',
        textDecoration: 'underline',
        cursor: 'pointer'
    },
    divider: {
        border: 'none',
        borderTop: '1px solid #666',
        margin: '4px 0'
    }
} as const

// ============================================================================
// SAFE DOM MANIPULATION HELPER FUNCTIONS
// ============================================================================

/**
 * Safely creates an HTML element with specified styles
 * Using createElement + style assignment instead of innerHTML prevents XSS
 */
const createElement = <K extends keyof HTMLElementTagNameMap>(
    tagName: K,
    styles?: Partial<CSSStyleDeclaration>,
    textContent?: string
): HTMLElementTagNameMap[K] => {
    const element = document.createElement(tagName)
    if (styles) {
        Object.assign(element.style, styles)
    }
    if (textContent !== undefined) {
        // textContent automatically escapes HTML entities, preventing XSS
        element.textContent = textContent
    }
    return element
}

/**
 * Creates a tooltip wrapper div with safe styling
 * @returns HTMLDivElement - The wrapper element (empty, ready for child elements)
 */
const createTooltipWrapper = (): HTMLDivElement => {
    return createElement('div', TOOLTIP_STYLES.wrapper)
}

/**
 * Creates a safe tooltip header element
 * Uses textContent instead of innerHTML to prevent XSS from date strings
 * @param dateStr - The date string to display (safely escaped via textContent)
 */
const createSafeTooltipHeader = (dateStr: string): HTMLDivElement => {
    return createElement('div', TOOLTIP_STYLES.header, dateStr)
}

/**
 * Creates a table header cell with safe text content
 * @param config - Header configuration with text and alignment
 */
const createTableHeaderCell = (config: TableHeaderConfig): HTMLTableCellElement => {
    const th = createElement('th', {textAlign: config.align})
    th.textContent = config.text // Safe: textContent escapes HTML
    return th
}

/**
 * Creates a table data cell with safe text content
 * @param config - Cell configuration with text, alignment, and optional styles
 */
const createTableDataCell = (config: TableCellConfig): HTMLTableCellElement => {
    const td = createElement('td', {textAlign: config.align, ...config.style})
    td.textContent = config.text // Safe: textContent escapes HTML
    return td
}

/**
 * Creates a safe anchor element for tooltip links
 * Prevents XSS by setting href and textContent separately
 * @param href - The link destination (should be validated/sanitized upstream)
 * @param text - The link text (safely escaped via textContent)
 */
const createSafeLink = (href: string, text: string): HTMLAnchorElement => {
    const link = createElement('a', TOOLTIP_STYLES.link)
    // Only allow safe URL schemes to prevent javascript: XSS
    if (href.startsWith('/') || href.startsWith('http://') || href.startsWith('https://')) {
        link.href = href
    } else {
        link.href = '#'
    }
    link.textContent = text // Safe: textContent escapes HTML
    link.onclick = (event) => event.stopPropagation()
    return link
}

/**
 * Creates a horizontal divider row for tables
 */
const createDividerRow = (): HTMLTableRowElement => {
    const tr = document.createElement('tr')
    const td = document.createElement('td')
    td.colSpan = 2
    const hr = createElement('hr', TOOLTIP_STYLES.divider)
    td.appendChild(hr)
    tr.appendChild(td)
    return tr
}

/**
 * Creates a complete table with headers and rows using safe DOM methods
 * All text content is escaped automatically via textContent assignment
 * @param headers - Array of header configurations
 * @param rows - Array of row configurations
 */
const createSafeTable = (
    headers: TableHeaderConfig[],
    rows: (TableRowConfig | 'divider')[]
): HTMLTableElement => {
    const table = createElement('table', TOOLTIP_STYLES.table)

    // Create thead
    const thead = document.createElement('thead')
    const headerRow = document.createElement('tr')
    headers.forEach((header) => {
        headerRow.appendChild(createTableHeaderCell(header))
    })
    thead.appendChild(headerRow)
    table.appendChild(thead)

    // Create tbody
    const tbody = document.createElement('tbody')
    rows.forEach((row) => {
        if (row === 'divider') {
            tbody.appendChild(createDividerRow())
        } else {
            const tr = document.createElement('tr')
            row.cells.forEach((cell) => {
                tr.appendChild(createTableDataCell(cell))
            })
            tbody.appendChild(tr)
        }
    })
    table.appendChild(tbody)

    return table
}

/**
 * Clears tooltip content safely and returns the wrapper for new content
 * Using replaceChildren() is safer than innerHTML = '' as it properly removes event listeners
 */
const clearAndGetWrapper = (tooltipEl: TooltipElement): HTMLDivElement => {
    // Clear existing content safely
    tooltipEl.replaceChildren()

    // Create new wrapper
    const wrapper = createTooltipWrapper()
    tooltipEl.appendChild(wrapper)
    return wrapper
}

// ============================================================================
// TOOLTIP ELEMENT MANAGEMENT
// ============================================================================

const getOrCreateTooltip = (chart: Chart, enablePointerEvents = false): TooltipElement => {
    let tooltipEl = chart.canvas.parentNode?.querySelector(
        '.chartjs-tooltip-custom'
    ) as TooltipElement | null

    if (!tooltipEl) {
        tooltipEl = document.createElement('div') as TooltipElement
        tooltipEl.className = 'chartjs-tooltip-custom'
        tooltipEl._isHovered = false
        Object.assign(tooltipEl.style, {
            pointerEvents: 'none',
            position: 'absolute',
            transform: 'translate(-50%, 0)',
            transition: 'all .1s ease',
            zIndex: '1000'
        })
        chart.canvas.parentNode?.appendChild(tooltipEl)
    }

    if (enablePointerEvents) {
        tooltipEl.style.pointerEvents = 'auto'
        tooltipEl.onmouseenter = () => {
            tooltipEl!._isHovered = true
        }
        tooltipEl.onmouseleave = () => {
            tooltipEl!._isHovered = false
            tooltipEl!.style.opacity = '0'
        }
    } else {
        tooltipEl.style.pointerEvents = 'none'
    }

    return tooltipEl
}

const hideTooltip = (tooltipEl: TooltipElement): void => {
    tooltipEl.style.opacity = '0'
}

const positionTooltip = (
    chart: Chart,
    tooltip: TooltipModel<'bar'>,
    tooltipEl: TooltipElement
): void => {
    const {offsetLeft: posX, offsetTop: posY} = chart.canvas
    tooltipEl.style.opacity = '0.9'
    tooltipEl.style.left = `${posX + tooltip.caretX}px`
    tooltipEl.style.top = `${posY + tooltip.caretY}px`
}

const capitalizeFirst = (str: string): string =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

// ============================================================================
// TOOLTIP HANDLERS
// ============================================================================

/**
 * External tooltip handler for settlement charts
 * Uses safe DOM manipulation to prevent XSS vulnerabilities
 *
 * Security: All dynamic values (dates, amounts, translations) are rendered via
 * textContent which automatically escapes HTML entities, preventing injection attacks.
 */
const externalTooltipHandler = (context: {chart: Chart; tooltip: TooltipModel<'bar'>}): void => {
    const {chart, tooltip} = context
    const tooltipEl = getOrCreateTooltip(chart)

    if (!tooltip || tooltip.opacity === 0) {
        hideTooltip(tooltipEl)
        return
    }

    const dataPoint = tooltip.dataPoints?.[0] as {raw?: {full?: SettlementTooltipData}} | undefined
    const full = dataPoint?.raw?.full
    if (!full) return

    const {settlementAmount, settlementDate, details, currency} = full

    // Clear existing content and create wrapper using safe DOM methods
    const wrapper = clearAndGetWrapper(tooltipEl)

    // Add header with formatted date (safe: textContent escapes HTML)
    wrapper.appendChild(createSafeTooltipHeader(moment(settlementDate).format('MMMM DD, YYYY')))

    // Build detail rows configuration for safe table construction
    const detailRowConfigs: TableRowConfig[] = details.map((detail: SettlementDetail) => ({
        cells: [
            {
                text: moment(detail.transactionDate).format(TITLE_DAY_MONTH_FORMAT),
                align: 'left' as const,
                style: {fontWeight: '400', fontSize: '12px', lineHeight: '1.2'}
            },
            {
                text: formatCurrency(detail.settlementAmount, detail.currency),
                align: 'right' as const,
                style: {fontWeight: '400'}
            }
        ]
    }))

    // Add total row configuration
    const totalRowConfig: TableRowConfig = {
        cells: [
            {text: i18next.t('globals:fields.total'), align: 'left'},
            {text: formatCurrency(settlementAmount, currency), align: 'right'}
        ]
    }

    // Create table with headers and all rows (details + divider + total)
    // Safe: createSafeTable uses textContent for all dynamic values
    const table = createSafeTable(
        [
            {text: i18next.t('globals:fields.paymentBatch'), align: 'left'},
            {text: i18next.t('globals:fields.amount'), align: 'right'}
        ],
        [...detailRowConfigs, 'divider', totalRowConfig]
    )

    wrapper.appendChild(table)
    positionTooltip(chart, tooltip, tooltipEl)
}

/**
 * External tooltip handler for payment charts with clickable link
 * Uses safe DOM manipulation to prevent XSS vulnerabilities
 *
 * Security: The link is created safely using createSafeLink which:
 * - Validates URL scheme to prevent javascript: XSS
 * - Uses textContent for link text to escape HTML entities
 */
const paymentsExternalTooltipHandler = (context: {
    chart: Chart
    tooltip: TooltipModel<'bar'>
}): void => {
    const {chart, tooltip} = context
    const tooltipEl = getOrCreateTooltip(chart, true)

    // Don't hide if mouse is hovering over the tooltip
    if (tooltipEl._isHovered) return

    if (!tooltip || tooltip.opacity === 0) {
        hideTooltip(tooltipEl)
        return
    }

    const dataPoint = tooltip.dataPoints?.[0] as {raw?: {full?: PaymentTooltipData}} | undefined
    const full = dataPoint?.raw?.full
    if (!full) return

    const {date, amount, status, currency} = full

    // Clear existing content and create wrapper using safe DOM methods
    const wrapper = clearAndGetWrapper(tooltipEl)

    // Add header with formatted date (safe: textContent escapes HTML)
    wrapper.appendChild(createSafeTooltipHeader(moment(date).format(DATE_FORMAT)))

    // Create table structure safely
    const table = createElement('table', TOOLTIP_STYLES.table)

    // Create thead with safe text content
    const thead = document.createElement('thead')
    const headerRow = document.createElement('tr')

    // Status header - capitalizeFirst is safe as it only manipulates string case
    const statusHeader = createElement('th', {textAlign: 'left'})
    statusHeader.textContent = capitalizeFirst(status)
    headerRow.appendChild(statusHeader)

    // Settlement date header - translation strings are escaped via textContent
    const settlementHeader = createElement('th', {textAlign: 'right'})
    settlementHeader.textContent = i18next.t('globals:fields.settlementDate')
    headerRow.appendChild(settlementHeader)

    thead.appendChild(headerRow)
    table.appendChild(thead)

    // Create tbody with data row
    const tbody = document.createElement('tbody')
    const dataRow = document.createElement('tr')

    // Amount cell - formatCurrency output is escaped via textContent
    const amountCell = createElement('td', {textAlign: 'left'})
    amountCell.textContent = formatCurrency(amount, currency)
    dataRow.appendChild(amountCell)

    // Link cell - only shown when user has settlements permission
    if (hasPermissions([PermissionsMap.settlements.read, PermissionsMap.settlements.full])) {
        const linkCell = createElement('td', {textAlign: 'right'})
        const link = createSafeLink(
            '/settlements/per-transaction-date',
            i18next.t('globals:fields.viewInSettlements')
        )
        linkCell.appendChild(link)
        dataRow.appendChild(linkCell)
    }

    tbody.appendChild(dataRow)
    table.appendChild(tbody)

    wrapper.appendChild(table)
    positionTooltip(chart, tooltip, tooltipEl)
}

/**
 * External tooltip handler for payment count charts
 * Uses safe DOM manipulation to prevent XSS vulnerabilities
 *
 * Security: All dynamic values (dates, counts, status, translations) are rendered
 * via textContent which automatically escapes HTML entities.
 */
const paymentsCountExternalTooltipHandler = (context: {
    chart: Chart
    tooltip: TooltipModel<'bar'>
}): void => {
    const {chart, tooltip} = context
    const tooltipEl = getOrCreateTooltip(chart)

    if (!tooltip || tooltip.opacity === 0) {
        hideTooltip(tooltipEl)
        return
    }

    const dataPoint = tooltip.dataPoints?.[0] as
        | {raw?: {full?: PaymentCountTooltipData}}
        | undefined
    const full = dataPoint?.raw?.full
    if (!full) return

    const {date, count, status} = full

    // Clear existing content and create wrapper using safe DOM methods
    const wrapper = clearAndGetWrapper(tooltipEl)

    // Add header with formatted date (safe: textContent escapes HTML)
    wrapper.appendChild(createSafeTooltipHeader(moment(date).format(DATE_FORMAT)))

    // Create table with safe DOM construction
    // The count value is converted to string and escaped via textContent
    const table = createSafeTable(
        [{text: capitalizeFirst(status), align: 'left'}],
        [
            {
                cells: [
                    {text: i18next.t('globals:fields.transactions'), align: 'left'},
                    {text: String(count), align: 'right'}
                ]
            }
        ]
    )

    wrapper.appendChild(table)
    positionTooltip(chart, tooltip, tooltipEl)
}

// ============================================================================
// CHART UTILITY FUNCTIONS
// ============================================================================

const getTooltipAmountWithCurrency = (currency: Currencies, amount: string | number): string => {
    let res = ''
    if (currency) res += getCurrencySymbol(currency)
    if (amount !== null) res += amount
    return res
}

const getDensityConfig = (transactionsLength: number) =>
    DENSITY_CHART_CONFIGS.find(({threshold}) => transactionsLength <= threshold)?.config ||
    DEFAULT_CHART_CONFIG

const createBaseBarChartOptions = (
    transactionsLength: number,
    externalHandler: (context: any) => void
): ChartOptions<'bar'> => {
    const {maxBarThickness, categoryPercentage, barPercentage} =
        getDensityConfig(transactionsLength)

    return {
        responsive: true,
        maintainAspectRatio: false,
        datasets: {
            bar: {
                minBarLength: 5,
                maxBarThickness,
                categoryPercentage,
                barPercentage
            }
        },
        scales: {
            x: {stacked: true, grid: {display: false}},
            y: {min: 0, grid: {display: true}}
        },
        plugins: {
            legend: {display: false},
            title: {
                display: true,
                text: '',
                padding: {top: 20},
                font: {size: 1}
            },
            tooltip: {
                enabled: false,
                position: 'nearest',
                external: externalHandler
            }
        }
    }
}

const createTooltipCallbacks = (renderTooltipTitle?: (tooltipItem: any) => string) => ({
    label: (context: any) =>
        getTooltipAmountWithCurrency(context.dataset.currency as Currencies, context.parsed.y),
    title: (tooltipItem: any) => {
        if (renderTooltipTitle) return renderTooltipTitle(tooltipItem)
        return tooltipItem[0]?.label
    }
})

// ============================================================================
// DOUGHNUT CHART CONFIG
// ============================================================================

export const doughnutChartConfig = {
    doughnutOptions: (centerTitle: string, centerSubtitle: string | number, renderLegend): object =>
        ({
            responsive: true,
            plugins: {
                legend: {display: false},
                title: {display: false}
            },
            cutout: '70%',
            radius: '100%',
            elements: {
                title: {
                    text: centerTitle,
                    color: colors.grey5,
                    fontStyle: getFontFamilyName(),
                    sidePadding: 20,
                    heightPadding: 0,
                    minFontSize: 12,
                    maxFontSize: 14,
                    lineHeight: 12,
                    fontSize: 12,
                    textAlign: 'center',
                    textBaseline: 'bottom'
                },
                subtitle: {
                    text: centerSubtitle,
                    color: colors.dark,
                    fontStyle: getFontFamilyName(),
                    sidePadding: 0,
                    heightPadding: 10,
                    minFontSize: 14,
                    maxFontSize: 18,
                    lineHeight: 14,
                    fontSize: 14,
                    textAlign: 'center',
                    textBaseline: 'top'
                }
            },
            legendCallback: renderLegend
        }) as ChartOptions,

    getDoughnutPlugins: [
        {
            id: 'text',
            beforeDraw: (chart) => {
                if (chart.config?.options?.elements?.title?.text) {
                    drawCtxCenterText(chart, 'title')
                }
                if (chart.config?.options?.elements?.subtitle?.text) {
                    drawCtxCenterText(chart, 'subtitle')
                }
            }
        }
    ]
}

// ============================================================================
// BAR CHART CONFIG
// ============================================================================

export const barChartConfig = {
    barCompareOptions: ({title = 'Custom Chart Title', renderTooltipTitle}): ChartOptions => ({
        maintainAspectRatio: false,
        responsive: true,
        scales: {
            x: {stacked: true},
            y: {stacked: true}
        },
        plugins: {
            legend: {
                position: 'bottom' as LayoutPosition,
                labels: {
                    boxWidth: 8,
                    boxHeight: 8,
                    font: {size: 10, family: getFontFamilyName()},
                    padding: 10,
                    color: colors.dark
                },
                align: 'center' as 'center' | 'end' | 'start'
            },
            title: {
                position: 'top',
                display: true,
                text: title,
                align: 'start',
                font: {size: 14, family: getFontFamilyName()}
            },
            tooltip: {callbacks: createTooltipCallbacks(renderTooltipTitle)}
        }
    }),

    barOptions: ({
        title = 'Custom Chart Title',
        showTooltip = true,
        renderTooltipTitle = null
    }) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: false},
            title: {
                display: true,
                text: title,
                align: 'start',
                font: {size: 14, family: getFontFamilyName(), color: colors.dark}
            },
            tooltip: {
                enabled: showTooltip,
                position: 'nearest',
                callbacks: createTooltipCallbacks(renderTooltipTitle)
            }
        }
    }),

    barMobileOptions: {
        responsive: true,
        plugins: {
            legend: {display: false},
            title: {display: false}
        },
        scales: {
            x: {beginAtZero: true}
        }
    },

    barCustomTooltipOptions: (transactionsLength: number): ChartOptions<'bar'> =>
        createBaseBarChartOptions(transactionsLength, externalTooltipHandler),

    barPaymentsCustomTooltipOptions: (
        transactionsLength: number,
        dataType: string
    ): ChartOptions<'bar'> =>
        createBaseBarChartOptions(
            transactionsLength,
            dataType === 'count'
                ? paymentsCountExternalTooltipHandler
                : paymentsExternalTooltipHandler
        )
}
